import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { useAppContext } from "../../../contexts/AppContext";
import {
  fetchFeatureDistribution,
  fetchAllExtendedFeatures,
  resolveDatasetRef,
  colorForSite,
} from "../../../utils/featureDistribution";
import FeatureCard from "./FeatureCard";

// "Selected Sites Feature Distribution" panel for the Training Setup page.
// dev-0.7.21 contract — see Backend_To_Frontend/
//   2026-06-05_backend_feature_distribution_endpoint_integration_spec.md
//
// Trigger: explicit "Compare distributions" button (user-confirmed UX choice).
// Position: rendered AFTER SchemaPreCheckPanel in src/scenes/configs/index.jsx,
//   so the user sees schema validation status first, then explicit chart.
//
// State machine:
//   idle  → user hasn't requested a fetch yet (or selection invalidated since last)
//   loading → request in flight (in-flight is abortable on re-click)
//   ready → response in hand; render grid
//   error → backend returned a coded error; render message + retry button

// ── Brand accent (single source so we don't sprinkle hex literals) ──────
const ACCENT = 'var(--fx-secondary)';        // #0087B4 in both themes
const ACCENT_HOVER = 'var(--fx-secondary-600)';

// ── Friendly copy for per-site data-load warning codes ──────────────────
// Backend surfaces warnings[] with { code, capability_id, column? }. The raw
// "DATA_FILE_NOT_READABLE @ cap_tgh_tabular_brca" is meaningless to an
// operator, so we map each code to a human title + actionable hint. Unknown
// codes degrade gracefully to a title-cased version of the code.
const WARNING_COPY = {
  DATA_FILE_NOT_READABLE: {
    title: 'Data file could not be read',
    hint: 'The dataset may still be uploading, or the file is missing/corrupted at this site. It is excluded from the charts below.',
  },
  COLUMN_MISSING: {
    title: 'A column is missing',
    hint: 'This site is missing one of the compared columns, so it is skipped for the affected feature.',
  },
  DATA_EMPTY: {
    title: 'No rows to read',
    hint: 'The site reported an empty dataset for this selection.',
  },
};

const titleCaseCode = (code) =>
  String(code || 'Unknown issue')
    .toLowerCase()
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

const FeatureDistributionPanel = () => {
  const { selectedSites, siteCapabilities } = useAppContext();

  const datasetRef = useMemo(
    () => resolveDatasetRef(selectedSites, siteCapabilities),
    [selectedSites, siteCapabilities]
  );

  const completedSites = useMemo(
    () => (selectedSites || []).filter((s) => s && s.site_id && s.capability_id),
    [selectedSites]
  );

  // Stable signature: changes when dataset_ref or sites composition changes.
  // Used to invalidate any previously-fetched response when the user edits
  // their selection without re-clicking the button.
  const selectionSignature = useMemo(() => {
    const sorted = completedSites
      .map((s) => `${s.site_id}|${s.capability_id}`)
      .slice()
      .sort()
      .join(',');
    return `${datasetRef || ''}::${sorted}`;
  }, [datasetRef, completedSites]);

  const [state, setState] = useState({ kind: 'idle' });
  const [resultSig, setResultSig] = useState(null);   // signature at last fetch
  // dev-0.7.27 — "View more" toggle. 'default' = backend picks 8 sample_features;
  // 'expanded' = FE explicitly passes response.extended_features back so backend
  // returns the curated wider set (14-21 features per dataset). Toggles
  // re-issue the same endpoint; the 5-min request cache in
  // src/utils/featureDistribution.js makes round-trips free after the first
  // fetch of each mode for a given selection.
  const [viewMode, setViewMode] = useState('default');
  const inflightRef = useRef(null);

  // If selection changes after a result was loaded, mark it stale (collapse
  // back to idle so user re-confirms before re-rendering).
  useEffect(() => {
    if (state.kind === 'ready' && resultSig && resultSig !== selectionSignature) {
      setState({ kind: 'idle' });
      setViewMode('default');   // selection changed → reset expansion too
    }
  }, [selectionSignature, resultSig, state.kind]);

  // Cancel any in-flight fetch on unmount.
  useEffect(() => () => {
    if (inflightRef.current) {
      try { inflightRef.current.abort(); } catch (_) {}
      inflightRef.current = null;
    }
  }, []);

  const canFetch = !!datasetRef && completedSites.length >= 1 && completedSites.length <= 5;
  const tooFew = completedSites.length === 0;
  const tooMany = completedSites.length > 5;
  const refMismatch = !datasetRef && completedSites.length > 0;

  const handleFetch = async (overrideMode = null) => {
    if (!canFetch || state.kind === 'loading') return;
    if (inflightRef.current) {
      try { inflightRef.current.abort(); } catch (_) {}
    }
    const ctrl = new AbortController();
    inflightRef.current = ctrl;
    const sigAtRequest = selectionSignature;
    // Use overrideMode when caller explicitly asks (e.g. View more clicked
    // before viewMode state has propagated). Default to current viewMode.
    const effectiveMode = overrideMode ?? viewMode;
    // For "expanded" mode we replay extended_features back to backend so it
    // returns the curated wider set. We need extended_features from the
    // CURRENT response (default-mode fetch surfaces it at the top level);
    // fall back to [] if we don't have it yet (e.g. user opens expanded
    // before default ever fetched — shouldn't happen in current UI flow,
    // but defensive).
    const extendedFromCurrent =
      Array.isArray(state.data?.extended_features) ? state.data.extended_features : [];
    setState({ kind: 'loading' });
    try {
      const body = {
        dataset_ref: datasetRef,
        sites: completedSites.map((s) => ({
          site_id: s.site_id,
          capability_id: s.capability_id,
        })),
        include_label: true,
        // dev-0.7.23 — request human-friendly "nice" bin edges (Heckbert
        // {1, 2, 5} × 10^k stepping) instead of the default equal_width
        // float-stepping. Cleans up the x-axis labels (e.g. ages 30/40/50/60
        // instead of 35.0/35.8/36.6/...). Note from AKS sync §三:
        // `numeric_bins` is a HINT under round mode, not a hard count —
        // backend may return fewer bins than requested when no nice step
        // size hits the target. Our SVG histogram already reads
        // `feature.bin_edges` / `feature.distributions[].counts` directly,
        // so a different bin count flows through without code change.
        bin_strategy: 'round',
        numeric_bins: 10,
        // dev-0.7.27 — when user expands "View more", forward the curated
        // extended_features list back to backend. Backend uses this verbatim
        // (subject to MAX_VIZ_FEATURES cap — currently 9 server-side; when
        // backend bumps the cap, "View more" automatically returns more
        // charts in one shot, no FE change needed).
        ...(effectiveMode === 'expanded' && extendedFromCurrent.length > 0
          ? { features: extendedFromCurrent }
          : {}),
      };
      // dev-0.7.27 paginated path. When the user explicitly asks for the
      // expanded view AND we have extended_features in hand from the
      // previous default response, fan out backend calls until we've
      // collected the full curated set (defeats the server-side
      // MAX_VIZ_FEATURES=9 cap by walking through extended_features in
      // chunks). When backend bumps the cap to ~30 later, the first
      // iteration of fetchAllExtendedFeatures returns everything and
      // the loop exits immediately — zero FE change needed.
      const result =
        effectiveMode === 'expanded' && extendedFromCurrent.length > 0
          ? await fetchAllExtendedFeatures(
              // Don't pre-stuff `features` into base body — paginator
              // controls features per round.
              { ...body, features: undefined },
              extendedFromCurrent,
              { signal: ctrl.signal }
            )
          : await fetchFeatureDistribution(body, { signal: ctrl.signal });
      // If selection changed mid-flight, drop the result silently.
      if (sigAtRequest !== selectionSignature) return;
      setResultSig(sigAtRequest);
      setState({ kind: 'ready', data: result });
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      setState({ kind: 'error', code: err?.code || 'INTERNAL_ERROR', message: err?.message || 'Could not load distributions.' });
    } finally {
      inflightRef.current = null;
    }
  };

  // dev-0.7.27 — toggle handlers for "View more" / "Show less".
  // Both call handleFetch with the explicit target mode so we don't depend
  // on the setViewMode setState having flushed before the fetch reads it.
  const handleViewMore = () => {
    setViewMode('expanded');
    handleFetch('expanded');
  };
  const handleShowLess = () => {
    setViewMode('default');
    handleFetch('default');
  };

  // ── Disabled CTA hint ────────────────────────────────────────────────
  let ctaHint = null;
  if (tooFew) ctaHint = 'Select at least 1 site first.';
  else if (tooMany) ctaHint = 'Pick at most 5 sites for distribution comparison.';
  else if (refMismatch) ctaHint = 'Selected sites must belong to the same dataset.';

  // ── dev-0.7.27 imaging viz derived flags ─────────────────────────────
  // (a) LIDC soft failure: backend returns HTTP 200 with warnings[].code
  //     === 'IMAGING_REF_NOT_SUPPORTED_IN_VIZ'. We render a friendly
  //     "not supported yet" banner instead of the all-zero placeholder
  //     feature grid that backend ships for shape compatibility.
  // (b) ProstateX seg fallback: backend's sample_features_principle string
  //     contains the literal "Phase A-3.1" when the seg pipeline is in
  //     reduced-feature mode (3 charts instead of 9) because
  //     clinical_info.csv isn't uploaded per-site yet. We surface this
  //     as a small banner above the grid so the user understands why
  //     the chart count is lighter than the cls path.
  // (c) Toggle-button suppression: when extended_features doesn't add
  //     anything new vs the current default response (e.g. ProstateX seg
  //     has default=3 == extended=3), the "View more" CTA would be a
  //     no-op. Hide it.
  const dataWarnings = (state.kind === 'ready' && Array.isArray(state.data?.warnings))
    ? state.data.warnings
    : [];
  const isUnsupportedViz = dataWarnings.some(
    (w) => w && w.code === 'IMAGING_REF_NOT_SUPPORTED_IN_VIZ'
  );
  const hasPhaseA31Hint =
    state.kind === 'ready' &&
    typeof state.data?.sample_features_principle === 'string' &&
    state.data.sample_features_principle.includes('Phase A-3.1');
  const extendedCount =
    Array.isArray(state.data?.extended_features) ? state.data.extended_features.length : 0;
  const currentNonLabelCount =
    Array.isArray(state.data?.features)
      ? state.data.features.filter((f) => f?.feature !== '__label__').length
      : 0;
  // Show the toggle row if either:
  //   - in expanded mode (always need "Show less"), OR
  //   - in default mode and there's at least one extra feature to fetch.
  const showToggleRow =
    !isUnsupportedViz &&
    extendedCount > 0 &&
    (viewMode === 'expanded' || currentNonLabelCount < extendedCount);

  // ── capability_id → friendly site name lookup ────────────────────────
  // warnings[] carry capability_id only. Resolve to a readable site name via
  // the current selection (has both site_id + capability_id) crossed with the
  // response's sites[] (has display_name). Falls back to the raw id.
  const siteNameForCapability = useMemo(() => {
    const capToSiteId = new Map(
      completedSites.map((s) => [s.capability_id, s.site_id])
    );
    const siteIdToName = new Map(
      (state.kind === 'ready' && Array.isArray(state.data?.sites) ? state.data.sites : [])
        .map((s) => [s.site_id, s.display_name || s.site_id])
    );
    return (capId) => {
      const siteId = capToSiteId.get(capId);
      return (siteId && siteIdToName.get(siteId)) || siteId || capId;
    };
  }, [completedSites, state]);

  // Group the generic data-load warnings by code so the strip reads as a few
  // clear statements rather than one row per (code, site) pair.
  const groupedWarnings = useMemo(() => {
    const groups = new Map();
    for (const w of dataWarnings) {
      if (!w || !w.code || w.code === 'IMAGING_REF_NOT_SUPPORTED_IN_VIZ') continue;
      if (!groups.has(w.code)) groups.set(w.code, []);
      groups.get(w.code).push(w);
    }
    return [...groups.entries()];
  }, [dataWarnings]);

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <Card sx={{ mb: 2.5, p: 0, border: '1px solid var(--fx-border)', borderRadius: 2.5, boxShadow: 'var(--fx-shadow-sm)', bgcolor: 'var(--fx-paper)', overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
        {/* Header */}
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            {/* Icon badge */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: 'var(--fx-info-soft)',
                color: ACCENT,
                flexShrink: 0,
              }}
            >
              <InsertChartOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'var(--fx-text-strong)', lineHeight: 1.2 }}>
                  Selected Sites Feature Distribution
                </Typography>
                {/* Feature count badge once a result is in hand */}
                {state.kind === 'ready' && !isUnsupportedViz && currentNonLabelCount > 0 && (
                  <Chip
                    size="small"
                    label={`${currentNonLabelCount} feature${currentNonLabelCount === 1 ? '' : 's'}`}
                    sx={{
                      height: 19,
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      bgcolor: 'var(--fx-info-soft)',
                      color: 'var(--fx-info-text)',
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                )}
                {/* dev-0.7.27 — "?" tooltip explains backend's feature
                    curation principle. Only renders when backend has
                    actually surfaced sample_features_principle. */}
                {state.kind === 'ready' && state.data?.sample_features_principle && (
                  <Tooltip
                    title={state.data.sample_features_principle}
                    arrow
                    placement="top"
                    enterDelay={150}
                    leaveDelay={200}
                    componentsProps={{
                      tooltip: {
                        sx: { fontSize: '0.72rem', maxWidth: 360, lineHeight: 1.5, p: 1 },
                      },
                    }}
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: 15, color: 'var(--fx-text-muted)', cursor: 'help', '&:hover': { color: ACCENT } }}
                    />
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)' }}>
                Compare feature and label distributions across the sites selected for this training run.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
            {state.kind === 'ready' && (
              <Tooltip title="Re-fetch the latest distributions">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon fontSize="small" />}
                    onClick={() => handleFetch()}
                    disabled={!canFetch}
                    sx={{
                      fontWeight: 700,
                      textTransform: 'none',
                      borderRadius: 1.5,
                      color: ACCENT,
                      borderColor: 'var(--fx-info-border)',
                      '&:hover': { borderColor: ACCENT, bgcolor: 'var(--fx-info-soft)' },
                    }}
                  >
                    Refresh
                  </Button>
                </span>
              </Tooltip>
            )}
            {state.kind !== 'ready' && (
              <Tooltip title={ctaHint || 'Fetch per-feature distributions for the selected sites'}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={state.kind === 'loading' ? null : <InsertChartOutlinedIcon fontSize="small" />}
                    onClick={() => handleFetch()}
                    disabled={!canFetch || state.kind === 'loading'}
                    sx={{
                      bgcolor: ACCENT,
                      textTransform: 'none',
                      borderRadius: 1.5,
                      px: 2,
                      '&:hover': { bgcolor: ACCENT_HOVER },
                      fontWeight: 700,
                    }}
                  >
                    {state.kind === 'loading' ? 'Loading…' : 'Compare distributions'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Divider under header for visual separation from the body */}
        <Divider sx={{ my: 1.75, borderColor: 'var(--fx-border)' }} />

        {/* Site legend (only when we have a result, and not in
            unsupported-viz state — for LIDC the site n_samples is null
            and the legend would just say "n=—" which adds noise). */}
        {state.kind === 'ready' && !isUnsupportedViz && Array.isArray(state.data?.sites) && state.data.sites.length > 0 && (
          <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ gap: 0.75, mb: 1.75 }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--fx-text-muted)', mr: 0.25 }}>
              Sites
            </Typography>
            {state.data.sites.map((s, i) => (
              <Chip
                key={s.site_id || i}
                size="small"
                label={
                  <span style={{ fontSize: '0.7rem' }}>
                    <strong>{s.display_name || s.site_id}</strong>
                    <span style={{ color: 'var(--fx-text-muted)', marginLeft: 4 }}>n={s.n_samples ?? 'n/a'}</span>
                  </span>
                }
                sx={{
                  bgcolor: 'var(--fx-paper)',
                  border: '1px solid var(--fx-border)',
                  borderLeft: `3px solid ${colorForSite(s.site_id, i)}`,
                  borderRadius: 1,
                  '& .MuiChip-label': { px: 0.85 },
                }}
                avatar={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: colorForSite(s.site_id, i),
                      ml: 0.75,
                    }}
                  />
                }
              />
            ))}
          </Stack>
        )}

        {/* Top-level data-load warnings — humanized. dev-0.7.27: suppressed in
            the unsupported-viz case (dedicated card rendered below instead). */}
        {state.kind === 'ready' && !isUnsupportedViz && groupedWarnings.length > 0 && (
          <Box sx={{ mb: 1.75, p: 1.25, bgcolor: 'var(--fx-warning-soft)', border: '1px solid var(--fx-warning-border)', borderRadius: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
              <WarningAmberIcon sx={{ fontSize: 18, color: 'var(--fx-warning)' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--fx-warning-text)' }}>
                Some sites had data-load issues
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {groupedWarnings.map(([code, items]) => {
                const copy = WARNING_COPY[code] || { title: titleCaseCode(code), hint: null };
                const names = [...new Set(items.map((w) => siteNameForCapability(w.capability_id)))];
                return (
                  <Box key={code} sx={{ pl: 0.25 }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--fx-warning-text)' }}>
                      {copy.title}
                    </Typography>
                    {/* affected sites as small pills */}
                    <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5, mt: 0.4, mb: copy.hint ? 0.4 : 0 }}>
                      {names.map((name) => (
                        <Box
                          key={name}
                          component="span"
                          sx={{
                            display: 'inline-block',
                            fontSize: '0.66rem',
                            fontWeight: 600,
                            color: '#78350f',
                            bgcolor: 'rgba(217, 119, 6, 0.14)',
                            border: '1px solid var(--fx-warning-border)',
                            borderRadius: 0.75,
                            px: 0.6,
                            py: 0.1,
                          }}
                        >
                          {name}
                        </Box>
                      ))}
                    </Stack>
                    {copy.hint && (
                      <Typography sx={{ fontSize: '0.68rem', color: '#78350f', opacity: 0.9, lineHeight: 1.45 }}>
                        {copy.hint}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* dev-0.7.27 — LIDC "not supported yet" banner. */}
        {state.kind === 'ready' && isUnsupportedViz && (
          <Box
            sx={{
              p: 2.5,
              bgcolor: 'var(--fx-subtle)',
              border: '1px dashed var(--fx-border-strong)',
              borderRadius: 1.5,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--fx-text-secondary)', mb: 0.5 }}>
              Feature distribution viz isn&apos;t available yet for this dataset.
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'var(--fx-text-secondary)' }}>
              Backend support for{' '}
              <Box
                component="span"
                sx={{
                  fontFamily: 'var(--fx-font-mono)',
                  bgcolor: 'var(--fx-border)',
                  px: 0.5,
                  py: 0.1,
                  borderRadius: 0.5,
                }}
              >
                {state.data?.dataset_ref || 'this dataset_ref'}
              </Box>{' '}
              is planned for dev-0.7.28+. You can still proceed to Training Setup; only the comparison chart is gated.
            </Typography>
          </Box>
        )}

        {/* dev-0.7.27 — ProstateX seg "Phase A-3.1" hint. */}
        {state.kind === 'ready' && !isUnsupportedViz && hasPhaseA31Hint && (
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={0.75}
            sx={{
              mb: 1.75,
              p: 1.25,
              bgcolor: 'var(--fx-info-soft)',
              border: '1px solid var(--fx-info-border)',
              borderRadius: 1.5,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 18, color: 'var(--fx-info-text)', mt: '1px' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--fx-info-text)' }}>
                Reduced-feature view
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'var(--fx-info-text)' }}>
                Showing derived stats only. Full clinical fields (age / PSA / ISUP …) will appear automatically once backend completes the Phase A-3.1 mini upload, no FE change needed.
              </Typography>
            </Box>
          </Stack>
        )}

        {/* Body */}
        {state.kind === 'idle' && (
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: 'center',
              bgcolor: 'var(--fx-subtle)',
              border: '1px dashed var(--fx-border)',
              borderRadius: 2,
            }}
          >
            <InsertChartOutlinedIcon sx={{ fontSize: 30, color: 'var(--fx-text-muted)', mb: 0.75 }} />
            <Typography sx={{ fontSize: '0.85rem', color: 'var(--fx-text-secondary)', maxWidth: 440, mx: 'auto', lineHeight: 1.5 }}>
              {tooFew
                ? 'Pick sites above, then click Compare distributions to load per-feature distributions.'
                : tooMany
                ? `Currently ${completedSites.length} sites selected — pick at most 5 for distribution comparison.`
                : refMismatch
                ? 'Selected sites span multiple datasets; pick sites from one dataset to compare.'
                : 'Click Compare distributions to load per-feature distributions for the selected sites.'}
            </Typography>
          </Box>
        )}

        {state.kind === 'loading' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Box key={i} sx={{ p: 1.5, bgcolor: 'var(--fx-paper)', border: '1px solid var(--fx-border)', borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                  <Skeleton variant="text" width="55%" height={16} />
                  <Skeleton variant="rounded" width={48} height={14} />
                </Stack>
                <Skeleton variant="rounded" height={140} sx={{ mb: 0.75 }} />
                <Skeleton variant="text" width="40%" height={10} />
              </Box>
            ))}
          </Box>
        )}

        {state.kind === 'error' && (
          <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ p: 1.75, bgcolor: 'var(--fx-danger-soft)', border: '1px solid var(--fx-danger-border)', borderRadius: 1.5 }}>
            <ErrorOutlineIcon sx={{ fontSize: 22, color: 'var(--fx-danger)', mt: '1px' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--fx-danger-text)' }}>
                {state.message}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: 'var(--fx-danger-text)', opacity: 0.85, fontFamily: 'var(--fx-font-mono)', mt: 0.25 }}>
                code: {state.code}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => handleFetch()}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, borderColor: 'var(--fx-danger-border)', color: 'var(--fx-danger-text)', '&:hover': { borderColor: 'var(--fx-danger)', bgcolor: 'var(--fx-danger-soft)' } }}
            >
              Retry
            </Button>
          </Stack>
        )}

        {state.kind === 'ready' && !isUnsupportedViz && Array.isArray(state.data?.features) && state.data.features.length > 0 && (
          <>
            {/* dev-0.7.27 — expanded mode contains 14-21+ feature cards;
                wrap the grid in a fixed-max-height scroll container so the
                rest of the /configs page stays reachable. A sticky toolbar
                inside the scroll region keeps the count + "Show less" control
                visible while the user scrolls the cards. */}
            <Box
              sx={
                viewMode === 'expanded'
                  ? {
                      maxHeight: '70vh',
                      overflowY: 'auto',
                      border: '1px solid var(--fx-border)',
                      borderRadius: 2,
                      bgcolor: 'var(--fx-subtle)',
                    }
                  : {}
              }
            >
              {viewMode === 'expanded' && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    px: 1.25,
                    py: 0.75,
                    bgcolor: 'var(--fx-paper)',
                    borderBottom: '1px solid var(--fx-border)',
                  }}
                >
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fx-text-secondary)' }}>
                    Showing {currentNonLabelCount} of {extendedCount} curated features
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<UnfoldLessIcon fontSize="small" />}
                    onClick={handleShowLess}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      color: 'var(--fx-text-secondary)',
                      '&:hover': { bgcolor: 'var(--fx-subtle)', color: 'var(--fx-text-strong)' },
                    }}
                  >
                    Show less
                  </Button>
                </Stack>
              )}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                  gap: 1.5,
                  ...(viewMode === 'expanded' && { p: 1.25 }),
                }}
              >
                {/* Label card first if include_label=true */}
                {state.data.features
                  .slice()
                  .sort((a, b) => {
                    if (a.feature === '__label__') return -1;
                    if (b.feature === '__label__') return 1;
                    return 0;
                  })
                  .map((f) => (
                    <FeatureCard key={f.feature} feature={f} />
                  ))}
              </Box>
            </Box>

            {/* dev-0.7.27 — "View more" toggle (default mode only; in expanded
                mode the sticky toolbar above carries "Show less"). */}
            {showToggleRow && viewMode === 'default' && (
              <Stack direction="row" alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UnfoldMoreIcon fontSize="small" />}
                  onClick={handleViewMore}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 1.5,
                    color: ACCENT,
                    borderColor: 'var(--fx-info-border)',
                    '&:hover': { borderColor: ACCENT, bgcolor: 'var(--fx-info-soft)' },
                  }}
                >
                  {`View more (${state.data.extended_features.length} curated features)`}
                </Button>
              </Stack>
            )}

            {/* Partial-coverage chip: rendered < expected curated features. */}
            {showToggleRow && viewMode === 'expanded' && (() => {
              const rendered = state.data.features.filter((f) => f.feature !== '__label__').length;
              const expected = state.data.extended_features.length;
              if (rendered >= expected) return null;
              return (
                <Stack direction="row" alignItems="center" justifyContent="center" sx={{ mt: 1.5 }}>
                  <Tooltip
                    title={`${rendered} of ${expected} curated features rendered. Backend may have skipped columns missing at every site, or returned a partial set.`}
                    arrow
                    placement="top"
                  >
                    <Chip
                      size="small"
                      label={`${rendered}/${expected} features`}
                      sx={{
                        height: 22,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor: 'var(--fx-warning-soft)',
                        color: 'var(--fx-warning-text)',
                        border: '1px solid var(--fx-warning-border)',
                      }}
                    />
                  </Tooltip>
                </Stack>
              );
            })()}
          </>
        )}

        {state.kind === 'ready' && !isUnsupportedViz && (!state.data?.features || state.data.features.length === 0) && (
          <Box sx={{ py: 3, textAlign: 'center', color: 'var(--fx-text-muted)' }}>
            <Typography sx={{ fontSize: '0.82rem' }}>
              Backend returned no features to plot.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FeatureDistributionPanel;
